import { CollectionDescriptor, Root, Actions, ReadOptions, Users } from "firestar";
import CSConnect, { Casinos, Vouchers } from "./";
import { AddressType } from "./Address";
import lodash from 'lodash'
import firebase from 'firebase/compat/app'
import axios from 'axios'
import { CheckoutErrorOptionType } from "./Orders";
import { getStatesByCountry } from "./Utils";

export const GenderOptions = {
   null: null,
   'male': 'Male',
   'female': 'Female',
   'other': 'Other'
}

export const TierOptions = {
   1: 'Tier 1',
   2: 'Tier 2',
   3: 'Tier 3',
}

const StatesByCountry = getStatesByCountry()

const USStates = {} as { [key: string]: any }
StatesByCountry['United States'].forEach((state) => {
	USStates[state.code] = state.name
})

const CanadaStates = {} as { [key: string]: any }
StatesByCountry['Canada'].forEach((state) => {
	CanadaStates[state.code] = state.name
})

export const StateOptions = {
	'United States': USStates,
	'Canada': CanadaStates
}

export const CountryOptions = {
   'United States': 'United States',
   'Canada': 'Canada',
}

export type GenderType = keyof typeof GenderOptions
export type TierOptionType = keyof typeof TierOptions

interface ShippingAddressType extends AddressType {
   firstName: string,
   lastName: string,
   phoneNumber: string
}

export interface MemberType extends Root {
   name: string
   firstName: string,
   lastName: string,
   phoneNumber: string,
   email: string,
   cardNumber: string,
   gender: GenderType,
   age: number,
   address: AddressType,
   shippingAddress?: ShippingAddressType | null,
   casinoInfo: Casinos.CasinoInfo,
   metadata?: { [key: string]: any } | null
   voucherInfo?: null | Vouchers.VoucherInfo
   tier?: TierOptionType
   enabled?: boolean,
   createAccount?: boolean
   lastUpdatedAt?: firebase.firestore.Timestamp
   lastUpdatedBy?: Users.UserInfo,
   orderInfo?: {
      id: string,
      name: string
   }
}

namespace Members {

   export type Member = MemberType
   export type Gender = MemberType
   export type Address = AddressType
   export type ShippingAddress = ShippingAddressType

   export function getMemberWithCardNumber(
      casinoId: string,
      promotionId: string,
      cardNumber: string,
      readOptions?: ReadOptions
   ) {
      return new Promise<Member | undefined>(async (resolve, reject) => {
         try {
            const path = `casinos/${casinoId}/promotions/${promotionId}/members`
            const response = await Actions.readCollection<Member>(path, {
               where: [
                  {
                     fieldPath: 'cardNumber',
                     opStr: '==',
                     value: cardNumber
                  }
               ],
               orderBy: {
                  fieldPath: 'createdAt',
                  directionStr: 'desc'
               }
            }, readOptions)
            if (response.data.length > 0) {
               resolve(response.data[0])
            } else {
               resolve(undefined)
            }
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getMemberWithPhoneNumber(
      casinoId: string,
      promotionId: string,
      phoneNumber: string,
      readOptions?: ReadOptions
   ) {
      return new Promise<Member | undefined>(async (resolve, reject) => {
         try {
            const response = await Actions.readCollection<Member>(
               `casinos/${casinoId}/promotions/${promotionId}/members`,
               {
                  where: [
                     {
                        fieldPath: 'phoneNumber',
                        opStr: '==',
                        value: phoneNumber
                     }
                  ],
                  orderBy: {
                     fieldPath: 'createdAt',
                     directionStr: 'desc'
                  }
               },
               readOptions
            )
            if (response.data.length > 0) {
               resolve(response.data[0])
            } else {
               resolve(undefined)
            }
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getMemberFromCasino(
      casinoId: string,
      cardNumber: string
   ) {
      const path = `casinos/${casinoId}/members/${cardNumber}`
      return Actions.readDocument<Member>(path)
   }

   export function writeToCasino(casinoId: string, cardNumber: string, document: Members.Member) {
      const path = `casinos/${casinoId}/members/${cardNumber}`
      return Actions.writeDocument<Member>(path, document)
   }

   export function uploadMembersForPromotion(
      casinoId: string,
      promotionId: string,
      members: Member[],
      onProgress: (completed: number) => void
   ) {
      // return new Promise(async (resolve, reject) => {
      //    try {
      //       const baseUrl = CSConnect.getBaseUrl(true)
      //       const url = baseUrl + '/uploadMembersToPromotion'
      //       const body = {
      //          casinoId, 
      //          promotionId,
      //          members
      //       }
      //       const response = await axios.post(url, body, {
      //          headers: {
      //             'Content-Type': 'application/json',
      //             'Accept': '*/*',
      //          }
      //       })
      //       const responseJSON = response.data
      //       resolve(responseJSON)
      //    } catch (error) {
      //       reject(error)
      //    }
      // })
      return Actions.batchWrite(members.map((member) => {
         const collectionPath = `casinos/${casinoId}/promotions/${promotionId}/members`
         const memberId = member.id ?? Actions.generateIDForPath(collectionPath)
         return {
            path: `${collectionPath}/${memberId}`,
            document: member
         }
      }), onProgress)
   }

   export function getMembersForPromotion(
      casinoId: string,
      promotionId: string,
      descriptor?: CollectionDescriptor,
      readOptions?: ReadOptions
   ) {
      const path = `casinos/${casinoId}/promotions/${promotionId}/members`
      return Actions.readCollection<Member>(path, descriptor, readOptions)
   }

   export function getAllMembersForPromotion(
      casinoId: string,
      promotionId: string,
      descriptor?: CollectionDescriptor,
      readOptions?: ReadOptions
   ) {
      const path = `casinos/${casinoId}/promotions/${promotionId}/members`
      return Actions.readFullCollection<Member>(path, descriptor, readOptions)
   }

   export function get(
      casinoId: string,
      promotionId: string,
      memberId: string,
      readOptions?: ReadOptions
   ) {
      const path = `casinos/${casinoId}/promotions/${promotionId}/members/${memberId}`
      return Actions.readDocument<Member>(path, readOptions)
   }

   export function updateMember(
      casinoId: string,
      promotionId: string,
      memberId: string,
      member: Partial<Member>) {
      const path = `casinos/${casinoId}/promotions/${promotionId}/members/${memberId}`
      return Actions.updateDocument<Member>(path, member)
   }

   export function writeMember(
      casinoId: string,
      promotionId: string,
      memberId: string,
      member: Partial<Member>) {
      const path = `casinos/${casinoId}/promotions/${promotionId}/members/${memberId}`
      return Actions.writeDocument<Member>(path, member)
   }

   export function addMember(
      casinoId: string,
      promotionId: string,
      member: Member) {
      const path = `casinos/${casinoId}/promotions/${promotionId}/members`
      return Actions.addToCollection<Member>(path, member)
   }

   export function deleteMember(
      casinoId: string,
      promotionId: string,
      memberId: string) {
      const path = `casinos/${casinoId}/promotions/${promotionId}/members/${memberId}`
      return Actions.deleteDocument(path)
   }

   export function updateSearchTags(member: Member) {
      return Actions.appendSearchTags<Member>(lodash.cloneDeep(member), {
         splitKeys: [
            'firstName',
            'lastName'
         ],
         createMatches: true,
         includeDocumentId: true,
         includeSpecialCharacters: true,
         absoluteKeys: [
            'id',
            'cardNumber',
            'email',
            'phoneNumber'
         ]
      })
   }

   export function sendSMS(
      recipients: string,
      text: string
   ) {
      return new Promise(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()
            const url = baseUrl + '/sendSMSMessage'
            const body = {
               text,
               recipients
            }
            const response = await axios.post(url, body, {
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            const responseJSON = response.data
            resolve(responseJSON)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getPointsBalance(
      casinoId: string,
      promotionId: string,
      memberId: string
   ) {
      return new Promise(async (resolve, reject) => {
         try {
            const url = `https://plugin-apcpoints-getplayerbalance-hmkl72amwq-uc.a.run.app?casinoId=${casinoId}&promotionId=${promotionId}&memberId=${memberId}`
            console.log('GET Player Balance URL:', url)
            const response = await axios.post(url)
            const responseJSON = response.data
            console.log('GET Player Balance Response:', responseJSON)
            resolve(responseJSON)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function withdrawBalance(
      patronPin: number,
      amount: number,
      patronId: string,
      orderId: string,
      orderNumber: string,
      orderName: string
   ) {
      return new Promise<{
         metadata: { [key: string]: any }
      }>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()
            const url = baseUrl + '/withdrawPlayerBalance'
            const body = {
               patronPin,
               amount,
               patronId,
               orderId,
               orderNumber,
               orderName
            }
            const response = await axios.post(url, body, {
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            const responseJSON = response.data
            resolve(responseJSON)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function depositBalance(
      amount: number,
      patronId: string
   ) {
      return new Promise<{
         metadata: { [key: string]: any }
      }>(async (resolve, reject) => {
         try {
            const baseUrl = CSConnect.getBaseUrl()
            const url = baseUrl + '/depositPlayerBalance'
            const body = {
               amount,
               patronId,
            }
            const response = await axios.post(url, body, {
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            const responseJSON = response.data
            resolve(responseJSON)
         } catch (error) {
            reject(error)
         }
      })
   }

   export function prepareMember(
      cardNumber: string,
      casinoId: string,
      promotionId: string
   ) {
      return new Promise<{
         status: 'success' | 'failed',
         code?: CheckoutErrorOptionType
      }>(async (resolve, reject) => {
         try {
            const url = `https://plugin-apcPoints-resolveMember-hmkl72amwq-uc.a.run.app?cardNumber=${cardNumber}&casinoId=${casinoId}&promotionId=${promotionId}`
            const response = await axios.post(url, {
               headers: {
                  'Content-Type': 'application/json',
                  'Accept': '*/*',
               }
            })
            const responseJSON = response.data
            resolve(responseJSON)
         } catch (error) {
            reject(error)
         }
      })
   }
}

export default Members
